import type { CreateSupplierDto } from '@shared/dto/supplier/create-supplier.dto';
import type { UpdateSupplierDto } from '@shared/dto/supplier/update-supplier.dto';
import { inject } from 'inversify';
import { Injectable } from '../../decorators/injectable';
import { HttpService, type IHttpService } from '../http/http.service';
import type {
  DeleteResult,
  Supplier,
  SuppliersListParams,
  SuppliersListResponse,
} from './suppliers.types';

export interface ISuppliersService {
  search(params: SuppliersListParams): Promise<SuppliersListResponse>;
  create(dto: CreateSupplierDto): Promise<Supplier>;
  update(id: string, dto: UpdateSupplierDto): Promise<Supplier>;
  delete(id: string): Promise<DeleteResult>;
  exportPdf(): Promise<void>;
  merge(survivorId: string, duplicateId: string): Promise<Supplier>;
  restore(id: string): Promise<Supplier>;
}

@Injectable()
export class SuppliersService implements ISuppliersService {
  constructor(@inject(HttpService) private readonly http: IHttpService) {}

  async search(params: SuppliersListParams): Promise<SuppliersListResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null || value === '' || value === false) {
        continue;
      }
      query.set(key, String(value));
    }
    const qs = query.toString();
    return this.http.get<SuppliersListResponse>(`suppliers${qs ? `?${qs}` : ''}`);
  }

  async create(dto: CreateSupplierDto): Promise<Supplier> {
    return this.http.post<Supplier>('suppliers', dto);
  }

  async update(id: string, dto: UpdateSupplierDto): Promise<Supplier> {
    return this.http.put<Supplier>(`suppliers/${id}`, dto);
  }

  async delete(id: string): Promise<DeleteResult> {
    return this.http.delete<DeleteResult>(`suppliers/${id}`);
  }

  async merge(survivorId: string, duplicateId: string): Promise<Supplier> {
    return this.http.post<Supplier>('suppliers/merge', { survivorId, duplicateId });
  }

  async restore(id: string): Promise<Supplier> {
    return this.http.post<Supplier>(`suppliers/${id}/restore`);
  }

  async exportPdf(): Promise<void> {
    const response = await this.http.getBlob('suppliers/export');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'fornitori.pdf';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }
}
